import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiLogOut, FiGlobe, FiEdit2, FiTrash2, FiExternalLink, FiSearch, FiTrendingUp, FiShare2, FiStar, FiActivity, FiZap } = FiIcons;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { websites, addWebsite, deleteWebsite, loading } = useData();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    url: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addWebsite(formData);
      setFormData({ name: '', logo: '', url: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding website:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this website and all its pages?')) {
      try {
        await deleteWebsite(id);
      } catch (error) {
        console.error('Error deleting website:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Enhanced Header with Glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 lg:mb-12 bg-white/80 backdrop-blur-xl rounded-2xl lg:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 border border-white/20 space-y-4 lg:space-y-0"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6 w-full lg:w-auto">
            <div className="relative flex-shrink-0">
              <motion.img
                whileHover={{ scale: 1.1, rotate: 5 }}
                src="https://content.app-sources.com/s/51111663627105881/uploads/Downloaded/Untitled_design-9908745.png"
                alt="Angry Branding"
                className="h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16 rounded-xl shadow-lg"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-red-600 to-gray-900 bg-clip-text text-transparent">
                SEO Command Center
              </h1>
              <p className="text-gray-600 text-sm sm:text-base lg:text-lg mt-1">Monitor, optimize, and showcase your SEO excellence</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 lg:space-x-6 w-full sm:w-auto">
            <div className="text-left sm:text-right">
              <p className="text-xs sm:text-sm text-gray-500">Active Projects</p>
              <motion.p
                key={websites.length}
                initial={{ scale: 1.5, color: '#ef4444' }}
                animate={{ scale: 1, color: '#111827' }}
                className="text-xl sm:text-2xl font-bold"
              >
                {websites.length}
              </motion.p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={logout}
              className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 border border-red-200 hover:border-red-300 shadow-lg text-sm sm:text-base"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium">Logout</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Enhanced Add Website Button */}
        <div className="mb-6 sm:mb-8">
          <motion.button
            onClick={() => setShowAddForm(true)}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-red-700 hover:via-red-800 hover:to-red-900 transition-all duration-300 flex items-center justify-center space-x-3 shadow-xl hover:shadow-2xl text-sm sm:text-base"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>Add New Website</span>
            <SafeIcon icon={FiZap} className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
        </div>

        {/* Enhanced Add Website Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl lg:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-white/20"
          >
            <div className="flex items-center space-x-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiGlobe} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Add New Website</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Website Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-sm sm:text-base"
                    placeholder="Enter website name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-sm sm:text-base"
                    placeholder="https://example.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Logo URL <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="url"
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-sm sm:text-base"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-2 sm:pt-4">
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg text-sm sm:text-base"
                >
                  Create Website
                </motion.button>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddForm(false)}
                  className="w-full sm:w-auto bg-gray-100 text-gray-700 px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 text-sm sm:text-base"
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Enhanced Websites Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {websites.map((website, index) => (
            <motion.div
              key={website.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, scale: 1.03 }}
              className="bg-white/80 backdrop-blur-sm rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 border border-white/20 group relative"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 via-purple-500 to-blue-500"></div>
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex items-start justify-between mb-4 sm:mb-6">
                  <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                    {website.logo ? (
                      <motion.img
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        src={website.logo}
                        alt={website.name}
                        className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl object-cover shadow-md flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <SafeIcon icon={FiGlobe} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-gray-900 text-base sm:text-lg truncate">{website.name}</h3>
                      <p className="text-gray-500 text-xs sm:text-sm flex items-center space-x-2">
                        <SafeIcon icon={FiActivity} className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span>{website.pages?.length || 0} pages tracked</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1 sm:space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => navigate(`/admin/website/${website.id}`)}
                      className="p-1 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiEdit2} className="w-3 h-3 sm:w-4 sm:h-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(website.id)}
                      className="p-1 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="w-3 h-3 sm:w-4 sm:h-4" />
                    </motion.button>
                  </div>
                </div>

                {/* Enhanced SEO Stats */}
                <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg sm:rounded-xl p-2 sm:p-4 border border-green-200"
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                      <SafeIcon icon={FiTrendingUp} className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                      <span className="text-xs sm:text-sm font-medium text-green-800">Ranked</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-green-900">
                      {website.pages?.filter(p => p.ranking).length || 0}
                    </p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg sm:rounded-xl p-2 sm:p-4 border border-blue-200"
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                      <SafeIcon icon={FiSearch} className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                      <span className="text-xs sm:text-sm font-medium text-blue-800">Phrases</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-blue-900">
                      {website.pages?.reduce((sum, page) => sum + (page.keyPhrases?.length || 0), 0) || 0}
                    </p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg sm:rounded-xl p-2 sm:p-4 border border-purple-200"
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                      <SafeIcon icon={FiShare2} className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
                      <span className="text-xs sm:text-sm font-medium text-purple-800">Social</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-purple-900">
                      {website.pages?.filter(p => p.social_media_created).length || 0}
                    </p>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg sm:rounded-xl p-2 sm:p-4 border border-orange-200"
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                      <SafeIcon icon={FiStar} className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                      <span className="text-xs sm:text-sm font-medium text-orange-800">Score</span>
                    </div>
                    <p className="text-lg sm:text-2xl font-bold text-orange-900">
                      {website.pages?.length > 0 ? Math.round(((website.pages?.filter(p => p.ranking).length || 0) / website.pages.length) * 100) : 0}%
                    </p>
                  </motion.div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between space-y-2 sm:space-y-0 sm:space-x-3">
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    href={website.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors bg-blue-50 px-3 py-2 rounded-lg text-xs sm:text-sm"
                  >
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="font-medium">Visit Site</span>
                  </motion.a>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate(`/admin/website/${website.id}`)}
                    className="bg-gradient-to-r from-red-600 to-red-700 text-white px-4 sm:px-6 py-2 rounded-xl text-xs sm:text-sm font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg"
                  >
                    Manage
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {websites.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 sm:py-20 px-4"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl"
            >
              <SafeIcon icon={FiGlobe} className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </motion.div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Ready to dominate search results?</h3>
            <p className="text-gray-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Add your first website to start tracking SEO performance and creating stunning client reports.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg text-sm sm:text-base"
            >
              Get Started
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;